import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '../app/store'
import { defaultSurveySteps } from '../components/survey/SurveyWizard'
import { StepType } from '../components/wizard/QuestionsForm'
//import { ConfirmChatDeleteProps } from '../components/messaging/ConfirmChatDelete'

export interface ShowDialogInterface {
  //isShown: boolean,
  isForced?: boolean
  isForcedFromSurvey?: boolean
  isExited?: boolean
  step?: number
  screenTitle?: string
  singleScreen?: boolean
  hideUserAndLocationPinsForIncompleteProfile?: boolean
  //surveySteps?: StepType[]
}

export interface ShowConfirmChatDelete {
  confirmChatDeleteData?: any// ConfirmChatDeleteProps
  isShown: boolean
}

export interface DialogState {
  showProfile: undefined | ShowDialogInterface,
  showDeleteAccount: boolean
  showDataDeletionInstructions: boolean
  showSurvey: ShowDialogInterface
  showDisclaimer: boolean
  isToDoPinClicked: boolean
  isToDoSurveyClicked: boolean
  isToDoResourcesClicked: boolean
  showMapFilters: boolean
  showBanner: boolean
  // True when the app was entered via the /map/bladder-cancer/bcanstudy route (set once in AppLayout from the
  // route handle). Session-only (Redux, not client storage) — used to default the HW Merck-study consent checkbox.
  bcanStudy: boolean
  // True when the app was entered via the /researchconnect route (set once in AppLayout from the route
  // handle). Session-only, mirrors bcanStudy — defaults the HW Research Connect Direct consent checkbox.
  researchConnect: boolean
  // Whether the map search bar is expanded. Lives here (not SearchBar-local) so the top-center pin-count pill
  // can hide while the full-width search input is open; otherwise they overlap.
  searchExpanded: boolean
  // True from the moment account-deletion is confirmed until signout/reload. Persistent (Redux, not local/mutation
  // state) so the delete modal stays in a stable "Deleting…/Deleted" state across the auth-churn REMOUNTS that would
  // otherwise reset the local mutation state and re-show the (empty) confirm form → accidental double-submit.
  deleteTriggered: boolean
  showConfirmChatDelete: ShowConfirmChatDelete | boolean
  showDirectChat: any//SendbirdChatButtonProps
  showChatConvo: string
  showChatList: string
  showChatProfile: string,
  openChannelReady: boolean
  profileRefetchTriggered?: boolean
  lastRefreshUnreadMessageCount: number | undefined
  showAuthProfile: boolean
}

const initialState: DialogState =
{
  showProfile: undefined,
  showDeleteAccount: false,
  showDataDeletionInstructions: false,
  showSurvey: { isForced: false, isExited: false, isForcedFromSurvey: false },
  showDisclaimer: true,
  isToDoPinClicked: false,
  isToDoSurveyClicked: false,
  isToDoResourcesClicked: false,
  showMapFilters: false,
  showConfirmChatDelete: { isShown: false },
  showDirectChat: { showChat: false, appId: '', userId: '', channelUrl: '', accessToken: '' },
  showChatConvo: 'none',
  showChatList: 'flex',
  showChatProfile: 'none',
  showBanner: true,
  bcanStudy: false,
  researchConnect: false,
  searchExpanded: false,
  deleteTriggered: false,
  openChannelReady: false,
  lastRefreshUnreadMessageCount: undefined,
  showAuthProfile: false

};

export const dialogSlice = createSlice({
  name: 'dialog',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    showProfile: (state: any, action: PayloadAction<undefined | ShowDialogInterface>) => {
      state.showProfile = action.payload;
    },
    showDeleteAccount: (state: any, action: PayloadAction<boolean>) => {
      state.showDeleteAccount = action.payload;
    },
    showDataDeletionInstructions: (state: any, action: PayloadAction<boolean>) => {
      state.showDataDeletionInstructions = action.payload;
    },
    showSurvey: (state: any, action: PayloadAction<ShowDialogInterface>) => {
      //if( action.payload)      
      state.showSurvey = { ...state.showSurvey, ...action.payload };
      //state.showSurvey = {  isForced:   action.payload.isForced}
    },
    showDisclaimer: (state: any, action: PayloadAction<boolean>) => {
      state.showDisclaimer = action.payload;
    },
    checkToDoPin: (state: any) => {
      state.isToDoPinClicked = true
    },
    checkToDoSurvey: (state: any) => {
      state.isToDoSurveyClicked = true
    },
    checkToDoResources: (state: any) => {
      state.isToDoResourcesClicked = true
    },
    showConfirmChatDelete: (state: any, action: PayloadAction<ShowConfirmChatDelete | boolean>) => {
      if (typeof action.payload === 'boolean') {
        state.showConfirmChatDelete = { isShown: action.payload }
      } else {
        state.showConfirmChatDelete = action.payload
      }
    },
    showDirectChat: (state: any, action: PayloadAction<any/*SendbirdChatButtonProps*/>) => {
      state.showDirectChat = action.payload;
    },
    closeDirectChat: (state: any, action: PayloadAction<void>) => {
      state.showChatConvo = 'none';
      state.showChatList = 'flex';
      state.showChatProfile = 'none';
      state.showDirectChat = { ...state.showDirectChat, showChat: false }

    },
    selectChat: (state: any, action: PayloadAction<void>) => {
      state.showChatConvo = 'flex';
      state.showChatList = 'none';
      state.showChatProfile = 'none';
    },
    showChatProfile: (state: any, action: PayloadAction<void>) => {
      state.showChatConvo = 'none';
      state.showChatList = 'none';
      state.showChatProfile = 'flex';
    },
    showBanner: (state: any, action: PayloadAction<boolean>) => {
      state.showBanner = action.payload
    },
    setBcanStudy: (state: any, action: PayloadAction<boolean>) => {
      state.bcanStudy = action.payload
    },
    setResearchConnect: (state: any, action: PayloadAction<boolean>) => {
      state.researchConnect = action.payload
    },
    setSearchExpanded: (state: any, action: PayloadAction<boolean>) => {
      state.searchExpanded = action.payload
    },
    setDeleteTriggered: (state: any, action: PayloadAction<boolean>) => {
      state.deleteTriggered = action.payload
    },
    showAuthProfile: (state: any, action: PayloadAction<boolean>) => {
      state.showAuthProfile = action.payload
    },
    setOpenChannelReady: (state: any, action: PayloadAction<boolean>) => {
      state.openChannelReady = action.payload
    },
    setProfileRefetchTriggered: (state: any, action: PayloadAction<boolean>) => {
      state.profileRefetchTriggered = action.payload
    },
    setMessagingRefetchTriggered: (state: any, action: PayloadAction<boolean>) => {
      state.profileRefetchTriggered = action.payload
    },
    setLastRefreshUnreadMessageCount: (state: DialogState, action: PayloadAction<number>) => {
      state.lastRefreshUnreadMessageCount = action.payload//state.unreadMessageCount??0 +
    },

  },

});

export const { closeDirectChat, showDirectChat, showConfirmChatDelete, showProfile, showDeleteAccount, showDataDeletionInstructions, showSurvey, showDisclaimer, checkToDoPin, checkToDoSurvey, checkToDoResources, selectChat, showChatProfile, showBanner, setBcanStudy, setResearchConnect, setSearchExpanded, setDeleteTriggered, setOpenChannelReady,
  setProfileRefetchTriggered, setMessagingRefetchTriggered, setLastRefreshUnreadMessageCount, showAuthProfile
} = dialogSlice.actions;

export const dialogSelector = (state: RootState) => state.dialog;
export const lastRefreshUnreadMessageCountSelector = (state: RootState) => dialogSelector(state).lastRefreshUnreadMessageCount;
export default dialogSlice.reducer;
